import { InvestorDetailsGetInvestorLegalEntitiesQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { EDIT_INVESTOR_PAGE_ENTITY_GROUPING_REMOVE_GROUP_MUTATION } from '../../modalPages/entityGroupingModalPage/queries';

interface DeleteGroupProps {
  isOpen: boolean;
  currentGroup: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'][number] | null;
  investorLegalEntitiesRefetch: () => Promise<ApolloQueryResult<InvestorDetailsGetInvestorLegalEntitiesQuery>>;
  onClose: () => void;
}

const DeleteGroup = ({ isOpen, currentGroup, investorLegalEntitiesRefetch, onClose }: DeleteGroupProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [removeGroup] = useMutation(EDIT_INVESTOR_PAGE_ENTITY_GROUPING_REMOVE_GROUP_MUTATION);

  const handleDeleteGroup = async () => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      await removeGroup({
        variables: {
          id: currentGroup?.groupId
        }
      });

      await investorLegalEntitiesRefetch();
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Yes, Delete Group',
        onClick: handleDeleteGroup,
        disabled: isSubmitting
      }}
      cancelButtonName="No, Cancel and Do Not Delete"
      showCloseIcon={!isSubmitting}
      showCancelButton={!isSubmitting}
      isUseOutsideClick={!isSubmitting}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {isSubmitting ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this Group</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>
              This action will delete <b>{currentGroup?.entityName}</b>. All associated commitment data will be preserved with the grouped entities.
              But the group configuration will be permanently deleted. Are you sure that you want to proceed?
            </ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteGroup;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
